/**
 * AddDataActionDialog - Dialog para adicionar/editar ações de dados
 * 
 * Suporta: assign_department, assign_agent, add_tag, remove_tag, set_priority, 
 *          assign_phase, assign_global_queue
 * Formulário dinâmico baseado no tipo de ação
 * 
 * @version 2.2.0 - Adicionado suporte para assign_global_queue
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, User, Tag, Star, LayoutDashboard, MinusCircle, AlertTriangle, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RoutingActionType } from '@/pages/tenant/RoutingRules';
import { actionTypeConfig } from '../types';

// =====================================================
// INTERFACES
// =====================================================

// Interface para os dados da ação - inclui campos para remove_tag e assign_global_queue
interface DataActionData {
  department_id?: string;
  agent_id?: string;
  tag_id?: string;
  priority?: number;
  phase_id?: string;
  // v2.1: Campos para remove_tag
  remove_tag_mode?: 'specific' | 'all';
  // v2.2: Campos para assign_global_queue
  queue_id?: string;
}

interface AddDataActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: DataActionData) => void;
  actionType: RoutingActionType;
  initialValue?: Record<string, unknown>;
}

interface Department {
  id: string;
  name: string;
}

interface Agent {
  id: string;
  full_name: string | null;
  email: string;
}

interface ContactTag {
  id: string;
  name: string;
  color: string;
}

interface KanbanCategory {
  id: string;
  name: string;
}

interface KanbanPhase {
  id: string;
  name: string;
  color: string | null;
  category_id: string;
  category_name: string;
}

// v2.2: Interface para filas de distribuição
interface DistributionQueue {
  id: string;
  name: string;
  distribution_method: string;
}

// =====================================================
// COMPONENTE
// =====================================================

export function AddDataActionDialog({
  open,
  onOpenChange,
  onSave,
  actionType,
  initialValue,
}: AddDataActionDialogProps) {
  const { currentTenant } = useAuth();
  
  // v2.0.0: Membros do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [] } = useTenantMembers({ onlyActive: true });
  const agents: Agent[] = tenantMembers.map(m => ({
    id: m.user_id,
    full_name: m.full_name,
    email: m.email || '',
  }));
  
  // Dados carregados
  const [departments, setDepartments] = useState<Department[]>([]);
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [categories, setCategories] = useState<KanbanCategory[]>([]);
  const [phases, setPhases] = useState<KanbanPhase[]>([]);
  const [queues, setQueues] = useState<DistributionQueue[]>([]);  // v2.2: Filas globais
  
  // Valores selecionados
  const [departmentId, setDepartmentId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [tagId, setTagId] = useState('');
  const [priority, setPriority] = useState(5);
  const [phaseId, setPhaseId] = useState('');
  const [queueId, setQueueId] = useState('');  // v2.2: ID da fila selecionada
  
  // v2.1: Estado para modo de remoção de tag
  const [removeTagMode, setRemoveTagMode] = useState<'specific' | 'all'>('specific');

  // Configuração do tipo atual
  const config = actionTypeConfig[actionType];

  // Carrega dados necessários
  useEffect(() => {
    if (open && currentTenant?.id) {
      if (actionType === 'assign_department') fetchDepartments();
      // v2.1: add_tag e remove_tag usam a mesma lista de tags
      if (actionType === 'add_tag' || actionType === 'remove_tag') fetchTags();
      if (actionType === 'assign_phase') fetchPhasesWithCategories();
      // v2.2: Carregar filas de distribuição
      if (actionType === 'assign_global_queue') fetchQueues();
    }
  }, [open, currentTenant?.id, actionType]);

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      setDepartmentId((initialValue?.department_id as string) || '');
      setAgentId((initialValue?.agent_id as string) || '');
      setTagId((initialValue?.tag_id as string) || '');
      setPriority((initialValue?.priority as number) || 5);
      setPhaseId((initialValue?.phase_id as string) || '');
      setQueueId((initialValue?.queue_id as string) || '');  // v2.2: Reset da fila
      // v2.1: Reset do modo de remoção
      setRemoveTagMode((initialValue?.remove_tag_mode as 'specific' | 'all') || 'specific');
    }
  }, [open, initialValue]);

  // Departamentos via RPC server-side (filtra ativos client-side)
  const fetchDepartments = async () => {
    if (!currentTenant?.id) return;
    const { data } = await supabase.rpc('get_tenant_departments_full', {
      p_tenant_hint: currentTenant.id,
    });
    const list = (Array.isArray(data) ? data : []) as Array<any>;
    setDepartments(
      list
        .filter((d) => d.is_active !== false)
        .map((d) => ({ id: d.id, name: d.name }))
    );
  };

  // Tags via RPC server-side
  const fetchTags = async () => {
    if (!currentTenant?.id) return;
    const { data } = await supabase.rpc('get_tenant_contact_tags', {
      p_tenant_hint: currentTenant.id,
    });
    setTags((data || []) as Array<{ id: string; name: string; color: string }>);
  };

  // Categorias + fases via RPC consolidada
  const fetchPhasesWithCategories = async () => {
    if (!currentTenant?.id) return;

    const { data } = await supabase.rpc('get_tenant_kanban_categories_with_phases', {
      p_tenant_hint: currentTenant.id,
    });

    const list = (Array.isArray(data) ? data : []) as Array<any>;

    setCategories(
      list
        .filter((c) => c.is_active !== false)
        .map((c) => ({ id: c.id, name: c.name }))
    );

    const phasesWithCategory: KanbanPhase[] = list.flatMap((c) =>
      ((c.phases || []) as Array<any>).map((p) => ({
        id: p.id,
        name: p.name,
        color: p.color,
        category_id: p.category_id,
        category_name: c.name,
      }))
    );
    setPhases(phasesWithCategory);
  };

  // v2.2: Filas de distribuição via RPC server-side (filtros client-side)
  const fetchQueues = async () => {
    if (!currentTenant?.id) return;
    const { data } = await supabase.rpc('get_distribution_queues_with_stats', {
      p_tenant_hint: currentTenant.id,
    });
    const list = (Array.isArray(data) ? data : []) as Array<any>;
    setQueues(
      list
        .filter((q) => q.is_active && ['chat', 'all'].includes(q.context_type))
        .map((q) => ({ id: q.id, name: q.name, distribution_method: q.distribution_method }))
    );
  };

  // Handler de salvar
  const handleSave = () => {
    const data: DataActionData = {};
    
    switch (actionType) {
      case 'assign_department':
        if (!departmentId) return;
        data.department_id = departmentId;
        break;
      case 'assign_agent':
        if (!agentId) return;
        data.agent_id = agentId;
        break;
      case 'add_tag':
        if (!tagId) return;
        data.tag_id = tagId;
        break;
      // v2.1: Salvar dados de remove_tag
      case 'remove_tag':
        data.remove_tag_mode = removeTagMode;
        if (removeTagMode === 'specific') {
          if (!tagId) return;
          data.tag_id = tagId;
        }
        break;
      case 'set_priority':
        data.priority = priority;
        break;
      case 'assign_phase':
        if (!phaseId) return;
        data.phase_id = phaseId;
        break;
      // v2.2: Salvar dados de assign_global_queue
      case 'assign_global_queue':
        if (!queueId) return;
        data.queue_id = queueId;
        break;
    }
    
    onSave(data);
  };

  // Validação
  const isValid = () => {
    switch (actionType) {
      case 'assign_department': return !!departmentId;
      case 'assign_agent': return !!agentId;
      case 'add_tag': return !!tagId;
      // v2.1: Validação para remove_tag - modo "all" sempre válido, "specific" precisa de tag
      case 'remove_tag': return removeTagMode === 'all' || !!tagId;
      case 'set_priority': return priority >= 1 && priority <= 10;
      case 'assign_phase': return !!phaseId;
      // v2.2: Validação para assign_global_queue
      case 'assign_global_queue': return !!queueId;
      default: return false;
    }
  };

  // Ícone
  const getIcon = () => {
    switch (actionType) {
      case 'assign_department': return <Building2 className="h-5 w-5" />;
      case 'assign_agent': return <User className="h-5 w-5" />;
      case 'add_tag': return <Tag className="h-5 w-5" />;
      // v2.1: Ícone para remove_tag
      case 'remove_tag': return <MinusCircle className="h-5 w-5" />;
      case 'set_priority': return <Star className="h-5 w-5" />;
      case 'assign_phase': return <LayoutDashboard className="h-5 w-5" />;
      // v2.2: Ícone para fila global
      case 'assign_global_queue': return <Users className="h-5 w-5" />;
      default: return null;
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getIcon()}
            {initialValue ? `Editar ${config.label}` : `Adicionar ${config.label}`}
          </DialogTitle>
          <DialogDescription>
            Configure a ação de dados que será executada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Atribuir Departamento */}
          {actionType === 'assign_department' && (
            <div className="space-y-2">
              <Label>Departamento *</Label>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um departamento" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {departments.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhum departamento ativo. Crie departamentos primeiro.
                </p>
              )}
            </div>
          )}

          {/* Atribuir Agente */}
          {actionType === 'assign_agent' && (
            <div className="space-y-2">
              <Label>Agente *</Label>
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um agente" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.full_name || agent.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {agents.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhum agente disponível.
                </p>
              )}
            </div>
          )}

          {/* Adicionar Tag */}
          {actionType === 'add_tag' && (
            <div className="space-y-2">
              <Label>Tag *</Label>
              <Select value={tagId} onValueChange={setTagId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma tag" />
                </SelectTrigger>
                <SelectContent>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tags.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhuma tag cadastrada.
                </p>
              )}
            </div>
          )}

          {/* v2.1: Remover Tag de Contato */}
          {actionType === 'remove_tag' && (
            <div className="space-y-4">
              {/* Seleção de modo */}
              <div className="space-y-2">
                <Label>Modo de Remoção *</Label>
                <Select 
                  value={removeTagMode} 
                  onValueChange={(v: 'specific' | 'all') => setRemoveTagMode(v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="specific">Remover tag específica</SelectItem>
                    <SelectItem value="all">Remover todas as tags</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Seleção de tag (apenas modo específico) */}
              {removeTagMode === 'specific' && (
                <div className="space-y-2">
                  <Label>Tag a Remover *</Label>
                  <Select value={tagId} onValueChange={setTagId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma tag" />
                    </SelectTrigger>
                    <SelectContent>
                      {tags.map((tag) => (
                        <SelectItem key={tag.id} value={tag.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tags.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma tag cadastrada.
                    </p>
                  )}
                </div>
              )}
              
              {/* Alerta para modo "todas" */}
              {removeTagMode === 'all' && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Todas as tags serão removidas do contato quando esta regra for executada.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Definir Prioridade */}
          {actionType === 'set_priority' && (
            <div className="space-y-2">
              <Label>Prioridade (1-10) *</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => setPriority(Math.min(10, Math.max(1, parseInt(e.target.value) || 5)))}
              />
              <p className="text-xs text-muted-foreground">
                Quanto maior o número, maior a prioridade da conversa.
              </p>
            </div>
          )}

          {/* Atribuir Fase */}
          {actionType === 'assign_phase' && (
            <div className="space-y-2">
              <Label>Fase do Kanban *</Label>
              <Select value={phaseId} onValueChange={setPhaseId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma fase" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => {
                    const categoryPhases = phases.filter((p) => p.category_id === cat.id);
                    if (categoryPhases.length === 0) return null;
                    return (
                      <SelectGroup key={cat.id}>
                        <SelectLabel className="text-xs font-semibold text-muted-foreground px-2 py-1.5 bg-muted/50">
                          {cat.name}
                        </SelectLabel>
                        {categoryPhases.map((phase) => (
                          <SelectItem key={phase.id} value={phase.id}>
                            <div className="flex items-center gap-2">
                              {phase.color && (
                                <div
                                  className="w-3 h-3 rounded-full shrink-0"
                                  style={{ backgroundColor: phase.color }}
                                />
                              )}
                              {phase.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
              {phases.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhuma fase cadastrada.
                </p>
              )}
            </div>
          )}

          {/* v2.2: Atribuir Fila Global */}
          {actionType === 'assign_global_queue' && (
            <div className="space-y-2">
              <Label>Fila de Distribuição *</Label>
              <Select value={queueId} onValueChange={setQueueId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma fila" />
                </SelectTrigger>
                <SelectContent>
                  {queues.map((queue) => (
                    <SelectItem key={queue.id} value={queue.id}>
                      <div className="flex items-center gap-2">
                        <span>{queue.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {queue.distribution_method === 'round_robin' ? 'Rodízio' : 
                           queue.distribution_method === 'least_busy' ? 'Menos Ocupado' : 'Peso'}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {queues.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhuma fila ativa para chat. Crie uma fila de distribuição primeiro.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                A conversa será distribuída automaticamente para um agente elegível da fila.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!isValid()}>
            {initialValue ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
